import { ThemeEvents } from '@theme/events';

class ProductDescriptionExpandable extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #copyFeedbackTimeout;

  connectedCallback() {
    this.#abort?.abort();

    const body = this.querySelector('.product-description-expandable__body');
    const btn = this.querySelector('.product-description-expandable__toggle');
    if (!(body instanceof HTMLElement) || !(btn instanceof HTMLButtonElement)) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    const expand = () => {
      this.classList.add('is-expanded');
      btn.setAttribute('aria-expanded', 'true');
      btn.hidden = true;
    };

    const getCollapsedHeight = () => {
      const collapsedHeight = Number.parseFloat(
        getComputedStyle(this).getPropertyValue('--product-description-collapsed-height')
      );

      return Number.isNaN(collapsedHeight) ? 92 : collapsedHeight;
    };

    const syncOverflow = () => {
      requestAnimationFrame(() => {
        if (signal.aborted) return;

        const overflow = body.scrollHeight > getCollapsedHeight() + 2;
        btn.hidden = this.classList.contains('is-expanded') || !overflow;
        this.classList.toggle('has-overflow', overflow);

        if (!overflow) {
          this.classList.remove('is-expanded');
          btn.setAttribute('aria-expanded', 'false');
        }
      });
    };

    const scheduleCheck = () => {
      if (document.fonts?.ready) {
        document.fonts.ready.then(syncOverflow);
      } else {
        syncOverflow();
      }
    };

    this.classList.add('is-ready');
    scheduleCheck();
    window.addEventListener('resize', scheduleCheck, { signal });

    btn.addEventListener(
      'click',
      () => {
        expand();
      },
      { signal }
    );

    const skuEl = this.querySelector('[data-product-description-sku]');
    const skuRow = this.querySelector('.product-description-expandable__sku');
    const variantTarget = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"]');

    skuRow?.addEventListener(
      'click',
      async () => {
        if (!(skuEl instanceof HTMLElement) || !(skuRow instanceof HTMLButtonElement)) return;

        const sku = skuEl.textContent?.trim();
        if (!sku) return;

        try {
          await navigator.clipboard.writeText(sku);
          skuRow.classList.add('is-copied');

          if (this.#copyFeedbackTimeout) clearTimeout(this.#copyFeedbackTimeout);
          this.#copyFeedbackTimeout = setTimeout(() => {
            skuRow.classList.remove('is-copied');
            this.#copyFeedbackTimeout = undefined;
          }, 2000);
        } catch {
          // Clipboard API unavailable or denied
        }
      },
      { signal }
    );

    variantTarget?.addEventListener(
      ThemeEvents.variantUpdate,
      (event) => {
        const sku = event.detail?.resource?.sku || '';

        if (!(skuEl instanceof HTMLElement) || !(skuRow instanceof HTMLButtonElement)) return;

        if (sku) {
          skuEl.textContent = sku;
          skuRow.hidden = false;
        } else {
          skuRow.hidden = true;
        }
      },
      { signal }
    );
  }

  disconnectedCallback() {
    if (this.#copyFeedbackTimeout) clearTimeout(this.#copyFeedbackTimeout);
    this.#abort?.abort();
  }
}

if (!customElements.get('product-description-expandable')) {
  customElements.define('product-description-expandable', ProductDescriptionExpandable);
}
